// src/pages/BankAccountLookup.tsx
import {
  type FormEvent,
  type ReactNode,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  FaEye,
  FaEyeSlash,
  FaSearch,
  FaUniversity,
} from "react-icons/fa";
import { FiAlertTriangle } from "react-icons/fi";

import http from "../api/apiClient";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";

type EntityId = string | number;
type RequestStatus =
  | "idle"
  | "loading"
  | "found"
  | "not_found"
  | "error";

type IbanFetchStatus =
  | "idle"
  | "loading"
  | "found"
  | "error";

type PersonLike = {
  id?: EntityId;
  fullName?: string | null;
  fullname?: string | null;
  displayName?: string | null;
  display_name?: string | null;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

type BankAccountLike = {
  iban?: string | null;
  IBAN?: string | null;
  ibanMasked?: string | null;
  maskedIban?: string | null;
  maskedIBAN?: string | null;
  accountHolder?: string | null;
  accountHolderName?: string | null;
  holderName?: string | null;
};

type FamilyLike = {
  id?: EntityId;
  familyName?: string | null;
  name?: string | null;
  displayName?: string | null;
  display_name?: string | null;
  parent1?: PersonLike | null;
  parent2?: PersonLike | null;
  primaryParent?: PersonLike | null;
  secondaryParent?: PersonLike | null;
  parents?: PersonLike[] | null;
  parent1Id?: EntityId;
  parent2Id?: EntityId;
  parent1Name?: string | null;
  parent2Name?: string | null;
  accountHolder?: string | null;
  accountHolderName?: string | null;
  iban?: string | null;
  ibanMasked?: string | null;
  maskedIban?: string | null;
  bankAccount?: BankAccountLike | null;
  bankDetails?: BankAccountLike | null;
};

type LookupResponse = FamilyLike & {
  family?: FamilyLike | null;
  bankAccount?: BankAccountLike | null;
  bankDetails?: BankAccountLike | null;
  familyName?: string | null;
  parent1Name?: string | null;
  parent2Name?: string | null;
  parent1Id?: EntityId;
  parent2Id?: EntityId;
  accountHolder?: string | null;
  accountHolderName?: string | null;
  iban?: string | null;
  ibanMasked?: string | null;
  maskedIban?: string | null;
};

type LookupResult = {
  familyName: string;
  parent1: string;
  parent2: string;
  parentIds: EntityId[];
  accountHolder: string;
  maskedIban: string;
};

type DetailRowProps = {
  label: string;
  value?: string | null;
  action?: ReactNode;
};

const maskIban = (value: unknown): string => {
  if (typeof value !== "string") return "";

  const compact = value.replace(/\s+/g, "").trim();

  if (!compact) return "";

  return `**** **** **** ${compact.slice(-4)}`;
};

const pickFirstString = (
  ...values: unknown[]
): string => {
  for (const value of values) {
    if (typeof value !== "string") continue;

    const trimmed = value.trim();

    if (trimmed) return trimmed;
  }

  return "";
};

const formatPersonName = (
  person: PersonLike | null | undefined
): string => {
  if (!person) return "";

  return pickFirstString(
    person.fullName,
    person.fullname,
    person.displayName,
    person.display_name,
    person.name,
    [person.firstName, person.lastName]
      .filter(Boolean)
      .join(" "),
    [person.first_name, person.last_name]
      .filter(Boolean)
      .join(" ")
  );
};

const normalizeLookupResult = (
  data: LookupResponse
): LookupResult => {
  const family =
    data.family &&
    typeof data.family === "object"
      ? data.family
      : data;

  const bankAccount =
    data.bankAccount ||
    data.bankDetails ||
    family.bankAccount ||
    family.bankDetails ||
    {};

  const parents = Array.isArray(family.parents)
    ? family.parents
    : [
        family.parent1,
        family.parent2,
        family.primaryParent,
        family.secondaryParent,
      ].filter(
        (person): person is PersonLike =>
          Boolean(person)
      );

  return {
    familyName: pickFirstString(
      family.familyName,
      family.name,
      family.displayName,
      family.display_name,
      data.familyName
    ),
    parent1:
      pickFirstString(
        data.parent1Name,
        family.parent1Name
      ) || formatPersonName(parents[0]),
    parent2:
      pickFirstString(
        data.parent2Name,
        family.parent2Name
      ) || formatPersonName(parents[1]),
    parentIds: [
      data.parent1Id,
      family.parent1Id,
      parents[0]?.id,
      data.parent2Id,
      family.parent2Id,
      parents[1]?.id,
    ].filter(
      (value): value is EntityId =>
        value !== null && value !== undefined
    ),
    accountHolder: pickFirstString(
      bankAccount.accountHolder,
      bankAccount.accountHolderName,
      bankAccount.holderName,
      family.accountHolderName,
      family.accountHolder,
      data.accountHolderName,
      data.accountHolder
    ),
    maskedIban:
      pickFirstString(
        bankAccount.ibanMasked,
        bankAccount.maskedIban,
        bankAccount.maskedIBAN,
        family.ibanMasked,
        family.maskedIban,
        data.ibanMasked,
        data.maskedIban
      ) ||
      maskIban(
        bankAccount.iban ||
          family.iban ||
          data.iban
      ),
  };
};

const DetailRow = ({
  label,
  value,
  action,
}: DetailRowProps) => (
  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 dark:border-slate-800 dark:bg-slate-900">
    <dt className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
      {label}
    </dt>
    <dd className="mt-1.5 flex flex-wrap items-center gap-3 text-sm font-semibold text-slate-950 dark:text-white">
      <span>{value || "—"}</span>
      {action}
    </dd>
  </div>
);

export default function BankAccountLookup() {
  const { t } = useTranslation();

  const [iban, setIban] = useState("");
  const [status, setStatus] =
    useState<RequestStatus>("idle");
  const [result, setResult] =
    useState<LookupResult | null>(null);
  const [errorMessage, setErrorMessage] =
    useState("");
  const [fullIban, setFullIban] = useState("");
  const [ibanFetchStatus, setIbanFetchStatus] =
    useState<IbanFetchStatus>("idle");
  const [ibanFetchError, setIbanFetchError] =
    useState("");

  const handleSearch = async (
    event: FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const trimmedIban = iban.trim();

    if (!trimmedIban) {
      setStatus("idle");
      setResult(null);
      setErrorMessage("");
      return;
    }

    setStatus("loading");
    setResult(null);
    setErrorMessage("");
    setFullIban("");
    setIbanFetchStatus("idle");
    setIbanFetchError("");

    try {
      const { data } =
        await http.get<LookupResponse>(
          "/families/search-by-iban",
          {
            params: { iban: trimmedIban },
          }
        );

      setResult(
        normalizeLookupResult(data || {})
      );
      setStatus("found");
    } catch (caughtError: any) {
      if (
        caughtError?.response?.status === 404
      ) {
        setStatus("not_found");
        setErrorMessage(
          t(
            "family.bank_account_lookup.not_found",
            {
              defaultValue:
                "No family was found for this bank account number.",
            }
          )
        );
        return;
      }

      const mapped = mapHttpErrorToMessage(
        caughtError,
        t
      );

      setStatus("error");
      setErrorMessage(
        mapped?.message ||
          t("common.error", {
            defaultValue:
              "Something went wrong.",
          })
      );
    }
  };

  const handleFetchFullIban = async () => {
    if (
      !result?.parentIds.length ||
      ibanFetchStatus === "loading"
    ) {
      return;
    }

    setIbanFetchStatus("loading");
    setIbanFetchError("");

    for (const parentId of result.parentIds) {
      try {
        const { data } = await http.get<BankAccountLike>(
          `/bank-accounts/by-parent/${encodeURIComponent(
            String(parentId)
          )}`
        );

        const fetchedIban = pickFirstString(
          data?.iban,
          data?.IBAN
        );

        if (fetchedIban) {
          setFullIban(fetchedIban);
          setIbanFetchStatus("found");
          return;
        }
      } catch (caughtError: any) {
        if (
          caughtError?.response?.status === 404
        ) {
          continue;
        }

        const mapped = mapHttpErrorToMessage(
          caughtError,
          t
        );

        setIbanFetchError(
          mapped?.message ||
            t("common.error", {
              defaultValue:
                "Something went wrong.",
            })
        );
        setIbanFetchStatus("error");
        return;
      }
    }

    setIbanFetchStatus("error");
    setIbanFetchError(
      t(
        "family.bank_account_lookup.fetch_iban_not_found",
        {
          defaultValue:
            "The IBAN could not be retrieved.",
        }
      )
    );
  };

  const handleHideFullIban = () => {
    setFullIban("");
    setIbanFetchStatus("idle");
    setIbanFetchError("");
  };

  return (
    <main className="lds-page-enter px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] rounded-[32px] border border-slate-200/80 bg-white p-4 shadow-[0_24px_70px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-950 sm:p-5">
        <div className="space-y-6">
          <section className="relative overflow-hidden rounded-[32px] border border-amber-200/80 bg-gradient-to-r from-amber-50 via-orange-50/40 to-amber-50 px-7 py-8 shadow-sm dark:border-amber-900/60 dark:from-amber-950/30 dark:via-orange-950/20 dark:to-amber-950/30 sm:px-10">
            <FaUniversity
              aria-hidden="true"
              className="pointer-events-none absolute right-10 top-1/2 hidden -translate-y-1/2 text-[7rem] text-amber-200 dark:text-amber-900/50 sm:block"
            />

            <div className="relative z-10 flex min-w-0 items-center gap-6">
              <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-amber-200 to-orange-50 text-4xl text-amber-700 shadow-lg dark:border-slate-900 dark:from-amber-800 dark:to-orange-950 dark:text-amber-100">
                <FaUniversity aria-hidden="true" />
              </div>

              <div className="min-w-0 max-w-3xl">
                <p className="text-sm font-bold uppercase tracking-[0.14em] text-amber-700 dark:text-amber-300">
                  {t("tools.overview.cards.bankAccountLookup.category", {
                    defaultValue: "Finance",
                  })}
                </p>
                <h1 className="mt-2 text-4xl font-bold text-slate-950 dark:text-white">
                  {t("family.bank_account_lookup.title", {
                    defaultValue:
                      "Bank account lookup",
                  })}
                </h1>
                <p className="mt-4 text-base text-slate-600 dark:text-slate-300">
                  {t(
                    "family.bank_account_lookup.description",
                    {
                      defaultValue:
                        "Find a family or parent by bank account number.",
                    }
                  )}
                </p>
              </div>
            </div>
          </section>

          <div className="flex items-start gap-3 rounded-[20px] border border-amber-200 bg-amber-50 px-4 py-3.5 text-sm text-amber-900 shadow-sm dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
            <FiAlertTriangle
              aria-hidden="true"
              className="mt-0.5 shrink-0 text-lg"
            />
            <p>
              {t(
                "family.bank_account_lookup.privacy_warning",
                {
                  defaultValue:
                    "Bank account information is privacy-sensitive. All searches are audited.",
                }
              )}
            </p>
          </div>

          <section className="overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-950">
            <div className="flex items-center gap-3 border-b border-slate-200/80 px-5 py-5 dark:border-slate-800 lg:px-6">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/70 dark:text-indigo-300">
                <FaSearch aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-950 dark:text-white">
                  {t(
                    "family.bank_account_lookup.search_title",
                    {
                      defaultValue:
                        "Search bank account",
                    }
                  )}
                </h2>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {t(
                    "family.bank_account_lookup.search_description",
                    {
                      defaultValue:
                        "Enter the complete IBAN to search for a matching family.",
                    }
                  )}
                </p>
              </div>
            </div>

            <form
              onSubmit={handleSearch}
              className="grid grid-cols-1 gap-4 px-5 py-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:px-6"
            >
              <label className="min-w-0 flex-1">
                <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t(
                    "family.bank_account_lookup.iban_label",
                    {
                      defaultValue: "IBAN",
                    }
                  )}
                </span>
                <input
                  type="text"
                  value={iban}
                  onChange={(event) =>
                    setIban(event.target.value)
                  }
                  autoComplete="off"
                  inputMode="text"
                  placeholder={t(
                    "family.bank_account_lookup.iban_placeholder",
                    {
                      defaultValue:
                        "NL00 BANK 0000 0000 00",
                    }
                  )}
                  className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium uppercase tracking-wide text-slate-950 shadow-sm outline-none transition placeholder:normal-case placeholder:tracking-normal placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-950"
                />
              </label>

              <button
                type="submit"
                disabled={
                  status === "loading" ||
                  !iban.trim()
                }
                className="inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:focus:ring-indigo-950"
              >
                <FaSearch aria-hidden="true" />
                {status === "loading"
                  ? t("common.loading", {
                      defaultValue:
                        "Loading…",
                    })
                  : t(
                      "family.bank_account_lookup.search_button",
                      {
                        defaultValue:
                          "Look up bank account",
                      }
                    )}
              </button>
            </form>
          </section>

          {status === "not_found" ? (
            <div className="rounded-[20px] border border-amber-200 bg-amber-50 px-4 py-3.5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              {errorMessage}
            </div>
          ) : null}

          {status === "error" ? (
            <div className="rounded-[20px] border border-rose-200 bg-rose-50 px-4 py-3.5 text-sm text-rose-900 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-100">
              {errorMessage}
            </div>
          ) : null}

          {status === "found" && result ? (
            <section className="overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-950">
              <div className="border-b border-slate-200/80 px-5 py-5 dark:border-slate-800 lg:px-6">
                <h2 className="text-base font-bold text-slate-950 dark:text-white">
                  {t(
                    "family.bank_account_lookup.results_heading",
                    {
                      defaultValue: "Result",
                    }
                  )}
                </h2>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {t(
                    "family.bank_account_lookup.results_description",
                    {
                      defaultValue:
                        "Matching family and bank account details.",
                    }
                  )}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-6 px-5 py-5 xl:grid-cols-3 lg:px-6">
                <div>
                  <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.12em] text-indigo-600 dark:text-indigo-300">
                    {t(
                      "family.bank_account_lookup.family_section",
                      {
                        defaultValue: "Family",
                      }
                    )}
                  </h3>
                  <DetailRow
                    label={t(
                      "family.bank_account_lookup.family_name",
                      {
                        defaultValue:
                          "Family name",
                      }
                    )}
                    value={result.familyName}
                  />
                </div>

                <div>
                  <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.12em] text-indigo-600 dark:text-indigo-300">
                    {t(
                      "family.bank_account_lookup.parents_section",
                      {
                        defaultValue: "Parents",
                      }
                    )}
                  </h3>
                  <div className="grid gap-3">
                    <DetailRow
                      label={t(
                        "family.bank_account_lookup.parent_1",
                        {
                          defaultValue:
                            "Parent 1",
                        }
                      )}
                      value={result.parent1}
                    />
                    <DetailRow
                      label={t(
                        "family.bank_account_lookup.parent_2",
                        {
                          defaultValue:
                            "Parent 2",
                        }
                      )}
                      value={result.parent2}
                    />
                  </div>
                </div>

                <div>
                  <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.12em] text-indigo-600 dark:text-indigo-300">
                    {t(
                      "family.bank_account_lookup.bank_details_section",
                      {
                        defaultValue:
                          "Bank details",
                      }
                    )}
                  </h3>

                  <div className="grid gap-3">
                    <DetailRow
                      label={t(
                        "family.bank_account_lookup.account_holder",
                        {
                          defaultValue:
                            "Account holder",
                        }
                      )}
                      value={result.accountHolder}
                    />

                    <DetailRow
                      label={t(
                        "family.bank_account_lookup.masked_iban",
                        {
                          defaultValue:
                            "Masked IBAN",
                        }
                      )}
                      value={
                        fullIban ||
                        result.maskedIban
                      }
                      action={
                        fullIban ? (
                          <button
                            type="button"
                            onClick={
                              handleHideFullIban
                            }
                            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-bold text-indigo-700 transition hover:bg-indigo-50 focus:outline-none focus:ring-4 focus:ring-indigo-100 dark:text-indigo-300 dark:hover:bg-indigo-950/50 dark:focus:ring-indigo-950"
                          >
                            <FaEyeSlash aria-hidden="true" />
                            {t(
                              "family.bank_account_lookup.hide_iban_button",
                              {
                                defaultValue:
                                  "Hide IBAN",
                              }
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              void handleFetchFullIban()
                            }
                            disabled={
                              ibanFetchStatus ===
                                "loading" ||
                              !result.parentIds.length
                            }
                            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-bold text-indigo-700 transition hover:bg-indigo-50 focus:outline-none focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:text-slate-400 dark:text-indigo-300 dark:hover:bg-indigo-950/50 dark:focus:ring-indigo-950"
                          >
                            <FaEye aria-hidden="true" />
                            {ibanFetchStatus ===
                            "loading"
                              ? t(
                                  "common.loading",
                                  {
                                    defaultValue:
                                      "Loading…",
                                  }
                                )
                              : t(
                                  "family.bank_account_lookup.fetch_iban_button",
                                  {
                                    defaultValue:
                                      "Retrieve IBAN",
                                  }
                                )}
                          </button>
                        )
                      }
                    />
                  </div>

                  {ibanFetchError ? (
                    <p className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
                      {ibanFetchError}
                    </p>
                  ) : null}
                </div>
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </main>
  );
}
